import { Link, NavLink, useMatch, useNavigate, useResolvedPath } from 'react-router-dom';
import { isSemrush } from '/src';

const fallbackBaseUrl = window.location.origin;

function getSemrushBaseUrl() {
  const semrushAppUrl = window.SEMRUSH_APP_URL;

  if (typeof semrushAppUrl !== 'string' || !semrushAppUrl) {
    return fallbackBaseUrl;
  }

  try {
    return new URL(semrushAppUrl).toString();
  } catch {
    return fallbackBaseUrl;
  }
}

function normalizeRelativePath(path) {
  if (typeof path !== 'string') {
    return path;
  }

  return path.startsWith('/') ? path.slice(1) : path;
}

function isAbsoluteHttpUrl(value) {
  return typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'));
}

function getSemrushBaseDirUrl() {
  const url = new URL(getSemrushBaseUrl());

  if (!url.pathname.endsWith('/')) {
    url.pathname = `${url.pathname}/`;
  }

  return url.toString();
}

function normalizeSearch(search) {
  if (!search) {
    return '';
  }

  return search.startsWith('?') ? search : `?${search}`;
}

function normalizeHash(hash) {
  if (!hash) {
    return '';
  }

  return hash.startsWith('#') ? hash : `#${hash}`;
}

function toSemrushAbsoluteUrl(to) {
  const semrushBaseDirUrl = getSemrushBaseDirUrl();

  if (isAbsoluteHttpUrl(to)) {
    return to;
  }

  if (typeof to === 'string') {
    return new URL(normalizeRelativePath(to), semrushBaseDirUrl).toString();
  }

  if (to && typeof to === 'object') {
    const basePath = new URL(semrushBaseDirUrl).pathname;
    const currentPath = window.location.pathname;
    const currentRelativePath = currentPath.startsWith(basePath)
      ? currentPath.slice(basePath.length)
      : normalizeRelativePath(currentPath);
    const pathname = normalizeRelativePath(to.pathname ?? currentRelativePath);
    const search = normalizeSearch(to.search ?? '');
    const hash = normalizeHash(to.hash ?? '');
    const url = new URL(pathname, semrushBaseDirUrl);
    url.search = search;
    url.hash = hash;

    return url.toString();
  }

  return window.location.href;
}

function isPlainLeftClick(event) {
  return event.button === 0 && !event.metaKey && !event.altKey && !event.ctrlKey && !event.shiftKey;
}

export function AppLink({ to, onClick, target, ...rest }) {
  return <AppRouterLink component={Link} to={to} onClick={onClick} target={target} {...rest} />;
}

export function AppNavLink({ to, onClick, target, ...rest }) {
  return <AppRouterLink component={NavLink} to={to} onClick={onClick} target={target} {...rest} />;
}

function AppRouterLink({ component: Component, to, onClick, target, ...rest }) {
  const navigate = useNavigate();
  const semrushHref = toSemrushAbsoluteUrl(to);
  const isNavLink = Component === NavLink;
  const resolvedPath = useResolvedPath(to);
  const isRootPath = resolvedPath.pathname === '/';
  const navMatch = useMatch({
    path: resolvedPath.pathname,
    end: rest.end ?? isRootPath,
    caseSensitive: rest.caseSensitive ?? false,
  });
  const navState = { isActive: Boolean(navMatch) };

  const {
    className,
    style,
    end: _end,
    caseSensitive: _caseSensitive,
    children,
    replace,
    state,
    relative,
    preventScrollReset,
    viewTransition,
    reloadDocument,
    ...anchorRest
  } = rest;

  const handleClick = (event) => {
    onClick?.(event);

    if (event.defaultPrevented) {
      return;
    }

    const isProtocolOrHostLink = typeof to === 'string' && (/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(to) || to.startsWith('//'));

    if (!isSemrush || !isPlainLeftClick(event) || target === '_blank' || isProtocolOrHostLink || reloadDocument) {
      return;
    }

    event.preventDefault();
    navigate(to, { replace, state, relative, preventScrollReset, viewTransition });
  };

  if (!isSemrush) {
    return <Component to={to} {...rest} target={target} onClick={handleClick} />;
  }

  const computedClassName = isNavLink && typeof className === 'function' ? className(navState) : className;
  const computedStyle = isNavLink && typeof style === 'function' ? style(navState) : style;
  const renderedChildren = isNavLink && typeof children === 'function' ? children(navState) : children;

  return (
    <a
      href={semrushHref}
      {...anchorRest}
      target={target}
      className={computedClassName}
      style={computedStyle}
      onClick={handleClick}
      aria-current={isNavLink && navState.isActive ? 'page' : undefined}
    >
      {renderedChildren}
    </a>
  );
}
